import { useTheme } from '@mui/material/styles';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Schema } from 'yup';
import { object, string } from 'yup';

import ContentLoader from '@components/ContentLoader';
import { FormikDropdownField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import { BLOOD_TYPE_OPTIONS } from '@constants/option-types/blood-type';

import {
  useCreateLabObservation,
  useUpdateLabObservation,
} from '@hooks/api/lab-result';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';

import { getBloodTypeObservationTemplate } from '@models/fhir-templates/observation';
import type { LabResultObservationRecord } from '@models/lab-result';

import { getBloodTypeDisplay } from '@utils/records';

import {
  RecordsUpdateStyle,
  TitleText,
} from '../TaskModalContent/TaskModalContent.style';

const messages = defineMessages({
  title: {
    id: 'bloodTypeForm.bloodType',
    defaultMessage: '{action} Blood Type',
  },
  publish: {
    id: 'GLOBAL.publish',
    defaultMessage: 'Publish',
  },
  update: {
    id: 'GLOBAL.update',
    defaultMessage: 'Update',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  bloodType: {
    id: 'vitalSigns.bloodType',
    defaultMessage: 'Blood Type',
  },
});

type FormValues = {
  bloodType: string;
  submit: string | null;
};

export default function BloodTypeModalContent({
  onSubmit,
  bloodType,
}: {
  onSubmit: () => void;
  bloodType?: LabResultObservationRecord;
}) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const bloodTypeDisplay = getBloodTypeDisplay(bloodType)?.toString();

  const {
    state: { activeProfile },
  } = useSession();

  const { palette } = useTheme();

  const [createLabObservation, createLabObservationLoading] =
    useCreateLabObservation();
  const [updateLabObservation, updateLabObservationLoading] =
    useUpdateLabObservation();

  const loading = createLabObservationLoading || updateLabObservationLoading;

  const handleClose = () => onSubmit();

  const initialValues: FormValues = {
    bloodType: bloodTypeDisplay ?? '',
    submit: null,
  };

  const validationSchema: Schema<Partial<FormValues>> = object().shape({
    bloodType: string().required(formatMessage(messages.required)),
  });

  async function handleSubmit(values: FormValues) {
    try {
      const patient_id = activeProfile?.patient_id;
      if (!patient_id) {
        throw new Error('No patient data');
      }
      // Create or update blood type laboratory observation
      if (initialValues.bloodType !== values.bloodType && values.bloodType) {
        const observation = getBloodTypeObservationTemplate(
          patient_id,
          values.bloodType,
          // Preserve creation date if blood type already exists, else add current date
          bloodType?.testDate || new Date().toISOString()
        );
        if (bloodType?.id) {
          await updateLabObservation({
            input: {
              id: bloodType.id,
              observation,
            },
          });
        } else {
          await createLabObservation({
            input: {
              ...observation,
            },
          });
        }
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      handleClose();
    }
  }

  return (
    <RecordsUpdateStyle>
      <TitleText>
        {formatMessage(messages.title, {
          action: bloodType ? 'Edit' : 'Add',
        })}
      </TitleText>
      <HorizontalRule />
      <ContentLoader lineCount={1} loading={loading}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, setFieldValue }) => (
            <Form>
              <FormikDropdownField
                label={formatMessage(messages.bloodType)}
                labelColor={palette.primary.main}
                name="bloodType"
                onChange={(value) => setFieldValue('bloodType', value)}
                options={BLOOD_TYPE_OPTIONS}
                required
              />

              <RoundedButton disabled={isSubmitting} type="submit">
                {formatMessage(
                  bloodTypeDisplay ? messages.update : messages.publish
                )}
              </RoundedButton>
            </Form>
          )}
        </Formik>
      </ContentLoader>
    </RecordsUpdateStyle>
  );
}
